import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Activity, Filter, Undo2, CheckCircle2, XCircle, User,
  Image as ImageIcon, FolderOpen, Trash2, Upload, Wand2, Droplets,
  Tag, FileText, Plus, Sparkles, Lightbulb, LogIn, Bookmark, ChevronDown,
  Send, RefreshCw, BarChart3, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * AdminActivityLog — the "Admin Activity Control Center" page.
 *
 * Shows every meaningful action across the platform with rich filters
 * (by editor, date range, action type, target, session, approval).
 * Admins can approve / reject (= restore) destructive edits, see
 * before-after JSON for any row, and drill into batches via session_id.
 */
const fadeUp = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.025, duration: 0.3 } }),
};

const ACTION_GROUPS = [
  { label: "Photos", items: [
    ["photo.upload", "Upload", Upload],
    ["photo.delete", "Delete", Trash2],
    ["photo.rotate", "Rotate", Wand2],
    ["photo.watermark", "Watermark", Droplets],
    ["photo.caption", "Caption", FileText],
    ["photo.tag", "Tag", Tag],
  ]},
  { label: "Galleries", items: [
    ["gallery.create", "Create", Plus],
    ["gallery.delete", "Delete", Trash2],
    ["gallery.publish", "Publish", CheckCircle2],
  ]},
  { label: "Events", items: [
    ["event.create", "Create", Plus],
    ["event.update", "Update", FileText],
    ["event.delete", "Delete", Trash2],
  ]},
  { label: "Bulk", items: [
    ["bulk.delete", "Delete", Trash2],
    ["bulk.rotate", "Rotate", Wand2],
    ["bulk.watermark", "Watermark", Droplets],
    ["bulk.caption", "Caption", FileText],
    ["bulk.tag", "Tag", Tag],
    ["bulk.export", "Export", FolderOpen],
    ["bulk.create_album", "Create Album", Sparkles],
  ]},
  { label: "Other", items: [
    ["suggestion.submit", "Suggestion", Lightbulb],
    ["suggestion.convert_to_job", "Convert→Job", Bookmark],
    ["auth.login", "Login", LogIn],
    ["ai.tool_use", "AI Tool", Sparkles],
  ]},
];

function actionMeta(action) {
  for (const g of ACTION_GROUPS) {
    for (const [k, l, Ic] of g.items) if (k === action) return { label: l, Icon: Ic, group: g.label };
  }
  return { label: action, Icon: Activity, group: "Other" };
}

function approvalBadge(status) {
  const map = {
    auto_approved: { txt: "auto-approved", cls: "text-white/40 bg-white/5" },
    pending:       { txt: "pending review", cls: "text-gold bg-gold/15" },
    approved:      { txt: "approved", cls: "text-green-300 bg-green-500/15" },
    rejected:      { txt: "rejected & restored", cls: "text-red-300 bg-red-500/15" },
  };
  const m = map[status] || map.auto_approved;
  return <span className={`text-[10px] font-body uppercase tracking-wider px-1.5 py-0.5 ${m.cls}`}>{m.txt}</span>;
}

export default function AdminActivityLog() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [actors, setActors] = useState([]);
  const [filters, setFilters] = useState({ actor: "", action: "", days: 30, approval_status: "" });
  const [expanded, setExpanded] = useState(null);
  const [actingOn, setActingOn] = useState(null);
  const [view, setView] = useState("timeline"); // "timeline" | "productivity"
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // Auth check
        const me = await api.get("/auth/me");
        if (me.data?.role !== "admin") {
          toast.error("Admin access required.");
          navigate(me.data?.role === "editor" ? "/editor" : "/admin");
          return;
        }
        await refresh();
        const [statsRes, actorsRes] = await Promise.allSettled([
          api.get("/admin/activity/stats", { params: { days: filters.days } }),
          api.get("/admin/activity/actors"),
        ]);
        if (statsRes.status === "fulfilled") setStats(statsRes.value.data);
        if (actorsRes.status === "fulfilled") setActors(actorsRes.value.data || []);
      } catch {
        toast.error("Please sign in.");
        navigate("/admin");
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    try {
      const r = await api.get("/admin/activity", {
        params: {
          actor: filters.actor || undefined,
          action: filters.action || undefined,
          days: filters.days,
          approval_status: filters.approval_status || undefined,
          limit: 200,
        },
      });
      setItems(r.data?.items || []);
    } catch { toast.error("Couldn't load activity."); }
  };

  useEffect(() => { if (!loading) refresh(); /* eslint-disable-next-line */ }, [filters]);

  const approveRow = async (id) => {
    setActingOn(id);
    try {
      await api.post(`/admin/activity/${id}/approve`);
      toast.success("Approved.");
      await refresh();
    } catch { toast.error("Approve failed."); }
    finally { setActingOn(null); }
  };
  const rejectRow = async (id) => {
    if (!window.confirm("Reject this action? If it was a delete, the photo/gallery will be restored from the snapshot.")) return;
    setActingOn(id);
    try {
      const r = await api.post(`/admin/activity/${id}/reject`);
      toast.success(r.data?.restored ? "Rejected — content restored." : "Rejected.");
      await refresh();
    } catch { toast.error("Reject failed."); }
    finally { setActingOn(null); }
  };

  const loadProductivityReport = async (force = false) => {
    setReportLoading(true);
    try {
      // GPT-5 narrative generation can take 30-60s on first call of the week.
      // Generous timeout so the admin doesn't see a transient error.
      const r = await api.get(`/admin/activity/productivity${force ? "?force=1" : ""}`, { timeout: 120000 });
      setReport(r.data);
      // If the backend kicked off a background regen, poll every 8s
      // until the fresh report lands. Bounded to 20 polls (~2.5 min).
      if (r.data?.regenerating) {
        toast.success("ARIA is rebuilding the report. Refreshing automatically...");
        let attempts = 0;
        const initialGeneratedAt = r.data.generated_at;
        const poll = async () => {
          attempts += 1;
          if (attempts > 20) return;
          try {
            const rr = await api.get("/admin/activity/productivity", { timeout: 30000 });
            if (!rr.data?.regenerating && rr.data?.generated_at !== initialGeneratedAt) {
              setReport(rr.data);
              toast.success("Fresh report ready.");
              return;
            }
          } catch { /* keep polling */ }
          setTimeout(poll, 8000);
        };
        setTimeout(poll, 8000);
      }
    } catch { toast.error("Couldn't load productivity report."); }
    finally { setReportLoading(false); }
  };

  const sendProductivityNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/admin/activity/productivity/send", null, { timeout: 30000 });
      toast.success(`Report sent via ${r.data.channel}.`);
    } catch { toast.error("Send failed."); }
    finally { setSending(false); }
  };

  const grouped = useMemo(() => {
    // Group by date (yyyy-mm-dd) for the timeline rendering.
    const buckets = {};
    items.forEach(it => {
      const date = (it.created_at || "").slice(0, 10);
      buckets[date] = buckets[date] || [];
      buckets[date].push(it);
    });
    return Object.entries(buckets).sort(([a],[b]) => b.localeCompare(a));
  }, [items]);

  if (loading) {
    return <div className="pt-20 min-h-screen bg-luxury-black flex items-center justify-center"><p className="text-gold font-body animate-pulse">Loading activity log...</p></div>;
  }

  return (
    <div data-testid="activity-log-page" className="pt-20 min-h-screen bg-luxury-black">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/dashboard")} data-testid="back-to-dashboard" className="text-white/40 hover:text-gold transition-colors"><ArrowLeft size={20} /></button>
            <div>
              <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Admin · Activity</p>
              <h1 className="font-heading text-2xl text-white flex items-center gap-2"><Activity size={20} className="text-gold" /> Activity Control Center</h1>
            </div>
          </div>
        </div>

        {/* Stat tiles */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <motion.div variants={fadeUp} custom={0} initial="hidden" animate="visible" className="bg-luxury-charcoal border border-white/5 p-4">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Total ({stats.window_days}d)</p>
              <p className="text-white font-heading text-2xl">{stats.total}</p>
            </motion.div>
            <motion.div variants={fadeUp} custom={1} initial="hidden" animate="visible" className="bg-luxury-charcoal border border-gold/30 p-4">
              <p className="text-[10px] font-body uppercase tracking-wider text-gold">Pending approval</p>
              <p className="text-white font-heading text-2xl">{stats.pending_approval}</p>
            </motion.div>
            <motion.div variants={fadeUp} custom={2} initial="hidden" animate="visible" className="bg-luxury-charcoal border border-white/5 p-4">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Editors active</p>
              <p className="text-white font-heading text-2xl">{stats.by_actor?.filter(a => a.role === "editor").length || 0}</p>
            </motion.div>
            <motion.div variants={fadeUp} custom={3} initial="hidden" animate="visible" className="bg-luxury-charcoal border border-white/5 p-4">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Top action</p>
              <p className="text-white font-heading text-lg">{stats.by_action?.[0]?.action || "—"}</p>
              <p className="text-white/40 font-body text-[10px]">{stats.by_action?.[0]?.count || 0}× in {stats.window_days}d</p>
            </motion.div>
          </div>
        )}

        {/* View tabs — toggle between raw timeline and ARIA's weekly digest */}
        <div className="flex gap-1 mb-4 border-b border-white/5" data-testid="activity-view-tabs">
          <button onClick={() => setView("timeline")} data-testid="view-tab-timeline" className={`flex items-center gap-2 px-4 py-2.5 font-body text-xs uppercase tracking-wider transition-colors border-b-2 ${view === "timeline" ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}>
            <Activity size={13} /> Timeline
          </button>
          <button onClick={() => { setView("productivity"); if (!report) loadProductivityReport(); }} data-testid="view-tab-productivity" className={`flex items-center gap-2 px-4 py-2.5 font-body text-xs uppercase tracking-wider transition-colors border-b-2 ${view === "productivity" ? "text-gold border-gold" : "text-white/40 border-transparent hover:text-white/70"}`}>
            <BarChart3 size={13} /> Editor Productivity
          </button>
        </div>

        {/* PRODUCTIVITY VIEW — ARIA's weekly editor breakdown */}
        {view === "productivity" && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="productivity-panel">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60">Weekly digest</p>
                <h2 className="text-white font-heading text-xl">Editor Productivity Report</h2>
                {report && (
                  <p className="text-white/40 text-xs font-body mt-1">
                    Week {report.week_key} · {report.window_days}-day window ·
                    {" "}{report.cached ? "cached" : "fresh"} ·
                    {" "}generated {new Date(report.generated_at).toLocaleString()}
                    {report.sent_via && <> · sent via <span className="text-white/60">{report.sent_via}</span></>}
                    {report.regenerating && (
                      <span data-testid="report-regenerating-badge" className="ml-2 text-gold animate-pulse">
                        · ARIA is rebuilding...
                      </span>
                    )}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => loadProductivityReport(true)} disabled={reportLoading} data-testid="refresh-report-btn" className="text-xs font-body uppercase tracking-wider text-white/60 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors disabled:opacity-50 flex items-center gap-1.5">
                  <RefreshCw size={12} /> Regenerate
                </button>
                <button onClick={sendProductivityNow} disabled={sending} data-testid="send-report-btn" className="text-xs font-body uppercase tracking-wider bg-gold text-luxury-black hover:bg-gold/90 px-3 py-2 font-medium flex items-center gap-1.5 transition-colors disabled:opacity-50">
                  <Send size={12} /> Send now
                </button>
              </div>
            </div>

            {reportLoading && !report ? (
              <div className="text-center py-16 bg-luxury-charcoal border border-gold/20">
                <Sparkles size={28} className="text-gold/40 mx-auto mb-3 animate-pulse" />
                <p className="text-white/40 font-body text-xs animate-pulse">ARIA is analysing this week's activity log...</p>
                <p className="text-white/30 font-body text-[10px] mt-1">First generation each week can take 30-60s.</p>
              </div>
            ) : report ? (
              <>
                {/* Hero stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-luxury-charcoal border border-white/5 p-4">
                    <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Total actions</p>
                    <p className="text-white font-heading text-2xl">{report.total_actions}</p>
                  </div>
                  <div className="bg-luxury-charcoal border border-white/5 p-4">
                    <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Est. work hours</p>
                    <p className="text-white font-heading text-2xl">{report.total_hours}h</p>
                  </div>
                  <div className="bg-luxury-charcoal border border-gold/30 p-4">
                    <p className="text-[10px] font-body uppercase tracking-wider text-gold">Top performer</p>
                    <p className="text-white font-heading text-sm leading-tight mt-1">{report.top_performer || "—"}</p>
                  </div>
                  <div className="bg-luxury-charcoal border border-white/5 p-4">
                    <p className="text-[10px] font-body uppercase tracking-wider text-white/40">Idle editors</p>
                    <p className="text-white font-heading text-2xl">{(report.inactive_editors || []).length}</p>
                  </div>
                </div>

                {/* ARIA narrative */}
                <div className="bg-luxury-charcoal border border-gold/20 p-6 mb-6" data-testid="aria-productivity-narrative">
                  <p className="text-gold text-[10px] font-body uppercase tracking-[0.25em] flex items-center gap-1.5 mb-3"><Sparkles size={12} /> ARIA's briefing</p>
                  <pre className="text-white/85 font-body text-sm leading-relaxed whitespace-pre-wrap">{report.narrative}</pre>
                </div>

                {/* Per-editor breakdown */}
                <h3 className="text-white font-heading text-lg mb-3">Per editor breakdown</h3>
                {(report.editors || []).length === 0 ? (
                  <div className="text-center py-12 border border-white/5">
                    <Clock size={32} className="text-white/15 mx-auto mb-3" />
                    <p className="text-white/40 font-body text-sm">No activity this week.</p>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="productivity-editors">
                    {report.editors.map((ed) => (
                      <div key={ed.email} className="bg-luxury-charcoal border border-white/5 p-4" data-testid={`productivity-editor-${ed.email}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div>
                            <p className="text-white text-sm font-body">{ed.email}</p>
                            <p className="text-white/40 text-[11px] font-body uppercase tracking-wider mt-0.5">{ed.role || "unknown"}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-gold font-heading text-lg">{ed.total_actions}</p>
                            <p className="text-white/40 text-[10px] font-body">~{(ed.estimated_minutes / 60).toFixed(1)}h</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-white/5">
                          {Object.entries(ed.actions || {}).slice(0, 8).map(([action, count]) => (
                            <span key={action} className="text-[10px] font-body text-white/50 bg-white/5 px-1.5 py-0.5">
                              {action.replace("photo.", "").replace("bulk.", "bulk·")}: <span className="text-white">{count}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Inactive editors */}
                {(report.inactive_editors || []).length > 0 && (
                  <div className="mt-6 p-4 bg-luxury-charcoal border border-white/5">
                    <p className="text-[10px] font-body uppercase tracking-wider text-white/40 mb-2">Inactive this week</p>
                    <div className="flex flex-wrap gap-2">
                      {report.inactive_editors.map((e) => (
                        <span key={e} className="text-xs font-body text-white/60 bg-white/5 border border-white/10 px-2 py-1">{e}</span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 border border-white/5">
                <BarChart3 size={36} className="text-white/15 mx-auto mb-3" />
                <p className="text-white/40 font-body text-sm">Click "Regenerate" to build this week's report.</p>
              </div>
            )}
          </motion.div>
        )}

        {/* TIMELINE VIEW (default) */}
        {view === "timeline" && <>


        {/* Filters */}
        <div className="bg-luxury-charcoal border border-white/5 p-4 mb-6">
          <div className="flex flex-wrap items-end gap-3" data-testid="activity-filters">
            <div className="flex-1 min-w-[180px]">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40 mb-1">Editor</p>
              <select data-testid="filter-actor" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-2 py-2 font-body focus:border-gold/50 outline-none">
                <option value="">All editors</option>
                {actors.map(a => <option key={a.email} value={a.email}>{a.email} ({a.role}, {a.count})</option>)}
              </select>
            </div>
            <div className="flex-1 min-w-[180px]">
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40 mb-1">Action</p>
              <select data-testid="filter-action" value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })} className="w-full bg-luxury-black border border-white/10 text-white text-sm px-2 py-2 font-body focus:border-gold/50 outline-none">
                <option value="">All actions</option>
                {ACTION_GROUPS.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map(([k,l]) => <option key={k} value={k}>{l}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
            <div>
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40 mb-1">Window</p>
              <select data-testid="filter-days" value={filters.days} onChange={(e) => setFilters({ ...filters, days: Number(e.target.value) })} className="bg-luxury-black border border-white/10 text-white text-sm px-2 py-2 font-body focus:border-gold/50 outline-none">
                <option value={1}>Today</option>
                <option value={7}>7 days</option>
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={365}>1 year</option>
              </select>
            </div>
            <div>
              <p className="text-[10px] font-body uppercase tracking-wider text-white/40 mb-1">Approval</p>
              <select data-testid="filter-approval" value={filters.approval_status} onChange={(e) => setFilters({ ...filters, approval_status: e.target.value })} className="bg-luxury-black border border-white/10 text-white text-sm px-2 py-2 font-body focus:border-gold/50 outline-none">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="auto_approved">Auto-approved</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          </div>
        </div>

        {/* Timeline */}
        {items.length === 0 ? (
          <div className="text-center py-24 border border-white/5">
            <Activity size={40} className="text-white/15 mx-auto mb-4" />
            <p className="text-white/40 font-body text-sm">No activity matching these filters.</p>
          </div>
        ) : (
          <div className="space-y-6" data-testid="activity-timeline">
            {grouped.map(([date, rows]) => (
              <div key={date}>
                <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold/60 mb-2">{date} · {rows.length} event(s)</p>
                <div className="space-y-1">
                  {rows.map((r, idx) => {
                    const { Icon, label } = actionMeta(r.action);
                    const isExpanded = expanded === r.id;
                    return (
                      <motion.div key={r.id} variants={fadeUp} custom={idx} initial="hidden" animate="visible" className={`bg-luxury-charcoal border ${r.approval_status === "pending" ? "border-gold/40" : "border-white/5"} hover:border-white/10 transition-colors`} data-testid={`activity-row-${r.id}`}>
                        <button onClick={() => setExpanded(isExpanded ? null : r.id)} className="w-full text-left p-3 flex items-center gap-3">
                          <Icon size={14} className="text-gold shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-body truncate">
                              <span className="text-white/50">{r.actor_email || "system"}</span>
                              <span className="text-white/30"> · {label}</span>
                              {r.target_label && <span className="text-gold/80"> · {r.target_label}</span>}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-white/30 text-[10px] font-body">{new Date(r.created_at).toLocaleTimeString()}</span>
                              {approvalBadge(r.approval_status)}
                              {r.session_id && <span className="text-white/30 text-[10px] font-body">session {r.session_id.slice(0, 10)}…</span>}
                              {r.actor_role && <span className="text-white/30 text-[10px] font-body uppercase">{r.actor_role}</span>}
                            </div>
                          </div>
                          <ChevronDown size={14} className={`text-white/30 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </button>
                        {isExpanded && (
                          <div className="border-t border-white/5 p-4 bg-luxury-black/50" data-testid={`activity-detail-${r.id}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-body">
                              {r.before && (
                                <div>
                                  <p className="text-red-300/70 uppercase tracking-wider text-[10px] mb-1">Before</p>
                                  <pre className="bg-red-500/5 border border-red-500/10 p-3 text-red-100/70 text-[11px] overflow-x-auto whitespace-pre-wrap max-h-48">{JSON.stringify(r.before, null, 2)}</pre>
                                </div>
                              )}
                              {r.after && (
                                <div>
                                  <p className="text-green-300/70 uppercase tracking-wider text-[10px] mb-1">After</p>
                                  <pre className="bg-green-500/5 border border-green-500/10 p-3 text-green-100/70 text-[11px] overflow-x-auto whitespace-pre-wrap max-h-48">{JSON.stringify(r.after, null, 2)}</pre>
                                </div>
                              )}
                              {!r.before && !r.after && (
                                <p className="text-white/40 italic">No before/after snapshot for this action.</p>
                              )}
                              {r.details && Object.keys(r.details).length > 0 && (
                                <div className="md:col-span-2">
                                  <p className="text-white/40 uppercase tracking-wider text-[10px] mb-1">Details</p>
                                  <pre className="bg-white/3 border border-white/5 p-3 text-white/60 text-[11px] overflow-x-auto whitespace-pre-wrap max-h-48">{JSON.stringify(r.details, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                            {r.approval_status === "pending" && (
                              <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                                <button onClick={() => approveRow(r.id)} disabled={actingOn === r.id} data-testid={`approve-${r.id}`} className="flex items-center gap-1.5 text-xs font-body bg-green-500/15 text-green-300 hover:bg-green-500/25 px-3 py-1.5 transition-colors disabled:opacity-50">
                                  <CheckCircle2 size={12} /> Approve
                                </button>
                                <button onClick={() => rejectRow(r.id)} disabled={actingOn === r.id} data-testid={`reject-${r.id}`} className="flex items-center gap-1.5 text-xs font-body bg-red-500/15 text-red-300 hover:bg-red-500/25 px-3 py-1.5 transition-colors disabled:opacity-50">
                                  <Undo2 size={12} /> Reject &amp; restore
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        </>}
      </div>
    </div>
  );
}
